import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, User } from 'lucide-react';
import blogPosts, { categories } from '../data/blogData';
import SEO from '../components/SEO';

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredPosts = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="blog-page">
      <SEO title="Blog" description="Expert insights on trademark registration, copyright law, patent filing, and intellectual property protection from Trademark Finity." path="/resources/blogs" keywords="trademark blog, IP law articles, copyright tips" />
      {/* Hero */}
      <section className="relative bg-primary overflow-hidden text-white py-12 md:py-20">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 md:mb-6 tracking-tight leading-tight">
            Trademark Insights &
            <span className="text-secondary"> Resources</span>
          </h1>
          <p className="text-sm sm:text-lg md:text-xl text-secondary-100 max-w-3xl mx-auto">
            Stay informed with expert articles, guides, and insights on trademark registration, protection, and intellectual property law
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="bg-white border-b border-slate-200 sticky top-[72px] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto gap-2 py-4 justify-center scrollbar-hide" data-testid="category-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`category-${cat.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-secondary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6 md:gap-8" data-testid="blog-grid">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/resources/blogs/${post.id}`}
                className="group w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.33%-1.5rem)]"
                data-testid={`blog-card-${post.id}`}
              >
                <article className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  {/* Image */}
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-secondary text-brand text-xs font-bold px-3 py-1.5 rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span>{post.date}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {post.readTime}
                      </span>
                    </div>

                    <h2 className="font-heading text-lg sm:text-xl font-bold text-primary mb-3 group-hover:text-secondary transition-colors leading-tight">
                      {post.title}
                    </h2>

                    <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">{post.author}</span>
                      </div>
                      <span className="text-secondary text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No articles found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
